import { useMutation, useQueryClient } from '@tanstack/react-query'

import { inviteKeys } from './query-key-factory'

import { paths } from '@/config/path'
import { MutationConfig } from '@/lib/react-query'
import { supabase } from '@/lib/supabase-client'
import { logger } from '@/logger'
import { BaseProfile } from '@/types/user'

type CreateInviteResponse = {
  inviteCode: string
  url: string
  expiresAt: string
}

export const createInvite = async ({ forProfile }: { forProfile?: BaseProfile }): Promise<CreateInviteResponse> => {
  const { data, error } = await supabase.rpc('create_invitation')

  if (error) {
    logger.error('Failed to create invitation', { errorMessage: error })
    throw error
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    throw new Error('No invitation data returned')
  }

  const result = data[0] as { invite_code: string; expires_at: string }
  const base = `${window.location.origin}${paths.auth.register.path}`
  const params = new URLSearchParams()
  params.append('invite', result.invite_code)
  if (forProfile) {
    params.append('profile', forProfile)
  }

  return {
    inviteCode: result.invite_code,
    url: `${base}?${params.toString()}`,
    expiresAt: result.expires_at,
  }
}

type UseCreateInviteOptions = {
  mutationConfig?: MutationConfig<typeof createInvite>
}

export const useCreateInvite = ({ mutationConfig }: UseCreateInviteOptions = {}) => {
  const queryClient = useQueryClient()

  const { onSuccess, ...restConfig } = mutationConfig || {}

  return useMutation({
    onSuccess: (...args) => {
      // Invalidate user invitations to refresh the list
      queryClient.invalidateQueries({
        queryKey: inviteKeys.userInvitations(),
      })
      // Also invalidate pending invitations
      queryClient.invalidateQueries({
        queryKey: inviteKeys.userPendingInvitations(),
      })
      onSuccess?.(...args)
    },
    ...restConfig,
    mutationFn: createInvite,
  })
}
